import { LicenseType, StatusCode } from '@/api/enumerations';
import useSnackbar from '@/hooks/useSnackbar';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  addAcceptanceDocuments,
  addContractDocument,
  createPublicNotice,
  deletePublicNotice,
  getNotice,
  updatePublicNotice,
} from '../../../../../../api/theHive/notices';
import {
  AcceptanceDocData,
  UpdatePublicNoticeData,
} from '../../../../../../api/theHive/notices/types';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import { DetailProps, DocumentEntry } from '../../NewNotice/types';

interface UseNoticeActionsProps {
  noticeId?: number;
  isNewNotice: boolean;
}

interface NoticeData {
  name: string;
  observation: string;
  startDate: dayjs.Dayjs | null;
  endDate: dayjs.Dayjs | null;
  openToAccreditation: boolean;
  isVisibleToProto?: boolean;
  contractFile?: File;
  extraDocuments?: DocumentEntry[];
  licenseTypes: LicenseType[];
}

interface FetchNoticeDetailsProps {
  noticeId?: number;
  isNewNotice: boolean;
}

interface FetchNoticeDetailsReturn {
  name: string;
  setName: React.Dispatch<React.SetStateAction<string>>;
  observation: string;
  setObservation: React.Dispatch<React.SetStateAction<string>>;
  startDate: Dayjs | null;
  setStartDate: React.Dispatch<React.SetStateAction<Dayjs | null>>;
  endDate: Dayjs | null;
  setEndDate: React.Dispatch<React.SetStateAction<Dayjs | null>>;
  isLoading: boolean;
  openToAccreditation: boolean;
  setOpenToAccreditation: (value: boolean) => void;
  isVisibleToProto: boolean;
  setIsVisibleToProto: (value: boolean) => void;
  contractFile: AcceptanceDocData | null;
  licenseTypes: LicenseType[];
  setLicenseTypes: React.Dispatch<React.SetStateAction<LicenseType[]>>;
}

interface UseNoticeActionsReturn {
  handleFormSubmit: (noticeData: NoticeData) => Promise<void>;
  isLoading: boolean;
}

export function useFetchNoticeDetails({
  noticeId,
  isNewNotice,
}: FetchNoticeDetailsProps): FetchNoticeDetailsReturn {
  const [name, setName] = useState<string>('');
  const [observation, setObservation] = useState<string>('');
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [openToAccreditation, setOpenToAccreditation] = useState(false);
  const [isVisibleToProto, setIsVisibleToProto] = useState(false);
  const [contractFile, setContractFile] = useState<AcceptanceDocData | null>(
    null
  );
  const [licenseTypes, setLicenseTypes] = useState<LicenseType[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { getErrorMessage } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();

  const fetchNoticeDetails = useCallback(async () => {
    if (isNewNotice || !noticeId) return;

    try {
      setIsLoading(true);
      const response = await getNotice(noticeId);
      if (response?.data) {
        const noticeData = response.data;
        setName(noticeData.name);
        setObservation(noticeData.observations || '');
        setOpenToAccreditation(noticeData.open_to_proto_accreditation ?? false);
        setIsVisibleToProto(noticeData.visible_to_proto ?? false);

        const contractData: AcceptanceDocData = {
          id: 0,
          notice_id: noticeId,
          name: 'Contrato de prestação de serviço',
          download_url: noticeData.contract_download_url || '',
          file_size: noticeData.contract_file_size || 0,
          original_filename: noticeData.contract_original_filename || '',
        };
        setContractFile(contractData);

        if (noticeData.start_date) {
          setStartDate(dayjs(noticeData.start_date));
        }
        if (noticeData.end_date) {
          setEndDate(dayjs(noticeData.end_date));
        }
      }
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noticeId, isNewNotice]);

  useEffect(() => {
    if (!isNewNotice) {
      fetchNoticeDetails();
    }
  }, [fetchNoticeDetails, isNewNotice]);

  return {
    name,
    setName,
    observation,
    setObservation,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    isLoading,
    openToAccreditation,
    setOpenToAccreditation,
    isVisibleToProto,
    setIsVisibleToProto,
    contractFile,
    licenseTypes,
    setLicenseTypes,
  };
}

export function useNoticeActions({
  noticeId,
  isNewNotice,
}: UseNoticeActionsProps): UseNoticeActionsReturn {
  const [isLoading, setIsLoading] = useState(false);

  const { getErrorMessage } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const validateDates = useCallback(
    (startDate: dayjs.Dayjs | null, endDate: dayjs.Dayjs | null): boolean => {
      if (startDate && endDate && startDate.isAfter(endDate)) {
        handleSnackbar(
          'A data de início não pode ser maior que a data final',
          true
        );
        return false;
      }
      return true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleSaveNotice = useCallback(
    async ({
      name,
      observation,
      startDate,
      endDate,
      openToAccreditation,
      contractFile,
      extraDocuments,
      licenseTypes,
    }: NoticeData): Promise<void> => {
      if (!validateDates(startDate, endDate)) return;

      const noticeData = {
        name,
        announcement_date: startDate
          ? startDate.toISOString().split('T')[0]
          : '',
        start_date: startDate ? startDate.toISOString().split('T')[0] : '',
        end_date: endDate ? endDate.toISOString().split('T')[0] : '',
        observations: observation,
        open_to_proto_accreditation: openToAccreditation,
        allowed_license_types: licenseTypes,
      };

      let createdNoticeId: number | null = null;

      try {
        const response: DetailProps = await createPublicNotice(noticeData);
        if (response.detail.status_code === StatusCode.OK) {
          createdNoticeId = response.data?.id ?? null;
        } else {
          throw new Error(response.detail.description || 'Erro desconhecido');
        }
      } catch (error) {
        handleSnackbar(getErrorMessage(error), true);
        return;
      }

      if (!createdNoticeId) return;

      try {
        if (contractFile) {
          const contractFormData = new FormData();
          contractFormData.append('file', contractFile);
          const contractResponse = await addContractDocument(
            createdNoticeId,
            contractFormData
          );
          if (contractResponse.detail.status_code !== StatusCode.OK) {
            throw new Error(
              contractResponse.detail.description || 'Erro ao enviar contrato'
            );
          }
        }

        if (extraDocuments && extraDocuments.length > 0) {
          const acceptanceFormData = new FormData();
          extraDocuments.forEach((doc) => {
            acceptanceFormData.append('names', doc.name);
            acceptanceFormData.append('original_filenames', doc.file.name);
            acceptanceFormData.append('files', doc.file);
          });
          const acceptanceResponse = await addAcceptanceDocuments(
            createdNoticeId,
            acceptanceFormData
          );
          if (acceptanceResponse.detail.status_code !== StatusCode.OK) {
            throw new Error(
              acceptanceResponse.detail.description ||
                'Erro ao enviar documentos de aceite'
            );
          }
        }

        handleSnackbar('Edital criado com sucesso', false);
        navigate(`/the-hive/notices/${createdNoticeId}`);
      } catch (error) {
        await deletePublicNotice(createdNoticeId);
        handleSnackbar(`${getErrorMessage(error)} Tente novamente.`, true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validateDates, navigate, getErrorMessage]
  );

  const handleUpdateNotice = useCallback(
    async ({
      name,
      observation,
      startDate,
      endDate,
      openToAccreditation,
      isVisibleToProto,
    }: NoticeData): Promise<void> => {
      if (!noticeId) {
        // eslint-disable-next-line no-console
        console.error('Notice ID is required for update');
        return;
      }

      if (!validateDates(startDate, endDate)) return;

      const updateData: UpdatePublicNoticeData = {
        name: name.trim(),
        observations: observation.trim(),
        start_date: startDate
          ? startDate.toISOString().split('T')[0]
          : undefined,
        end_date: endDate ? endDate.toISOString().split('T')[0] : undefined,
        open_to_proto_accreditation: openToAccreditation,
        visible_to_proto: isVisibleToProto || false,
      };

      try {
        const response: DetailProps = await updatePublicNotice(
          noticeId,
          updateData
        );
        if (response.detail.status_code === StatusCode.OK) {
          handleSnackbar('Edital atualizado com sucesso', false);
          navigate(`/the-hive/notices/${noticeId}`);
        }
      } catch (error) {
        handleSnackbar(getErrorMessage(error), true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [noticeId, validateDates, navigate]
  );

  const handleFormSubmit = useCallback(
    async (noticeData: NoticeData): Promise<void> => {
      try {
        setIsLoading(true);
        if (isNewNotice) {
          await handleSaveNotice(noticeData);
        } else {
          await handleUpdateNotice(noticeData);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [isNewNotice, handleSaveNotice, handleUpdateNotice]
  );

  return {
    handleFormSubmit,
    isLoading,
  };
}
